/**
 * Every playground refusal key the backend can put on the wire, mapped to the
 * `tenant.playground.*` locale key that tells the user what is unavailable and
 * why. The backend throws typed exceptions whose message IS the key
 * (`PlaygroundRestrictionException('playground.capability.bank-aggregator')`
 * renders `{"message": "playground.capability.bank-aggregator"}`), so a
 * refusal must never reach the user untranslated.
 *
 * `frontend/tests/unit/playground-i18n.spec.js` keeps this map honest in both
 * directions: every key thrown under `backoffice/` must appear here, and every
 * entry here must have a backend throw site (or a documented exception — see
 * `playground.capability.push`, which seal-06 suppresses server-side instead
 * of throwing).
 *
 * Consumed by the axios 403 interceptor (capability refusals surfaced as a
 * flash message on whichever screen triggered them), by the playground
 * components' own error handling, and by the creation page's 422 mapping.
 */
export default {
    'conflict.playground-already-exists': 'tenant.playground.error.already_exists',
    'conflict.playground-build-in-progress': 'tenant.playground.error.build_in_progress',
    'playground.not-a-playground': 'tenant.playground.error.not_a_playground',
    'playground.wipe.not-a-playground': 'tenant.playground.error.not_a_playground',
    'playground.provisioning.not-a-playground': 'tenant.playground.error.not_a_playground',
    'playground.profile.locked': 'tenant.playground.error.profile_locked',
    'playground.email.not-sent': 'tenant.playground.error.email_not_sent',
    'playground.capability.bank-aggregator': 'tenant.playground.error.bank_aggregator',
    'playground.capability.ai-employees': 'tenant.playground.error.ai_employees',
    'playground.capability.receipt-extraction': 'tenant.playground.error.receipt_extraction',
    'playground.capability.voice-calls': 'tenant.playground.error.voice_calls',
    'playground.capability.realtime': 'tenant.playground.error.realtime',
    'playground.capability.push': 'tenant.playground.error.push',
    'playground.capability.api-tokens': 'tenant.playground.error.api_tokens',
    'playground.capability.invitations': 'tenant.playground.error.invitations',
    'playground.jurisdiction-not-supported': 'tenant.playground.error.jurisdiction_not_supported',
    'playground.jurisdiction-has-no-playground-plan': 'tenant.playground.error.jurisdiction_no_plan',
};

/**
 * Server keys owned by a dedicated playground surface (the regenerate control
 * and the build-progress component both render their own inline message for
 * them). The global axios interceptor skips the generic-forbidden redirect for
 * every mapped 403 but only flashes the keys NOT listed here, so the owning
 * surface's inline message is never doubled by a toast.
 */
export const LOCALLY_SURFACED_KEYS = ['playground.not-a-playground'];
